import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, ChevronDown, ChevronRight, Edit2, ListOrdered, Upload, User, X } from 'lucide-react';
import { toast } from 'react-hot-toast';
import { useTeamStore } from '../../store/teamStore';
import { gameService, type Game, type GameLineupEntry } from '../../services/gameService';
import { supabase } from '../../lib/supabase';
import { playerCardService } from '../../services/playerCardService';
import { sportStatsService } from '../../services/sportStatsService';
import { parseGameChangerCsvToPlayerStats } from '../../services/gamechangerImport';
import { AddGameStatsModal } from '../player-cards/AddGameStatsModal';
import { getCalculatedStats, formatCalculatedStat } from '../../utils/statCalculations';
import type { Player } from '../../types';
import type { DepthChartEntry } from '../../types/depth-chart';
import { INJURY_STATUS } from '../../constants/injuries';
import type { InjuryStatus } from '../../constants/injuries';
import { InjuryBadge } from '../ui/InjuryBadge';

interface GameDetailViewProps {}

interface StatWithId {
  id: string;
  stat_key: string;
  stat_value: number;
}

const BATTING_CALC_KEYS = ['AVG', 'OBP', 'SLG', 'OPS'];
const PITCHING_CALC_KEYS = ['ERA', 'WHIP'];
const PITCHING_RAW_KEYS = new Set(['IP', 'W', 'L', 'SV', 'ER', 'K', 'H_Allowed', 'R_Allowed', 'BB_Allowed', 'HBP_Allowed', 'BF', 'Pitches', 'WP', 'Balk']);

/** Compact one-game stats summary for accordion on game details page only. Uses tiles; hides pitching section when no pitching data. */
function GameDetailsPlayerStatsSummary({
  rawStats,
  calculated
}: {
  playerId: string;
  rawStats: StatWithId[];
  calculated: Record<string, number>;
  sport?: string;
}) {
  const hasAny = rawStats.length > 0 || Object.keys(calculated).length > 0;
  if (!hasAny) {
    return <p className="text-xs text-gray-500">No stats recorded for this game.</p>;
  }
  const rawByKey = rawStats.reduce((acc, s) => {
    acc[s.stat_key] = (acc[s.stat_key] || 0) + s.stat_value;
    return acc;
  }, {} as Record<string, number>);

  const battingCalc = BATTING_CALC_KEYS.filter((k) => calculated[k] != null);
  const pitchingCalc = PITCHING_CALC_KEYS.filter((k) => calculated[k] != null);
  const rawBattingKeys = Object.keys(rawByKey).filter((k) => !PITCHING_RAW_KEYS.has(k) && !PITCHING_CALC_KEYS.includes(k)).sort();
  const rawPitchingKeys = Object.keys(rawByKey).filter((k) => PITCHING_RAW_KEYS.has(k)).sort();
  const hasPitchingData =
    pitchingCalc.length > 0 || rawPitchingKeys.some((k) => (rawByKey[k] ?? 0) !== 0);

  const battingRows: Array<{ label: string; value: string }> = [
    ...battingCalc.map((k) => ({ label: k, value: formatCalculatedStat(calculated[k], k) })),
    ...rawBattingKeys.map((k) => ({ label: k, value: String(Number(rawByKey[k])) })),
  ];
  const formatPitchingLabel = (statKey: string) =>
    statKey === 'Pitches' ? 'Total' : statKey.replace(/_Allowed$/, '');
  const pitchingRows: Array<{ key: string; label: string; value: string }> = [
    ...pitchingCalc.map((k) => ({ key: k, label: k, value: formatCalculatedStat(calculated[k], k) })),
    ...rawPitchingKeys.map((k) => ({ key: k, label: formatPitchingLabel(k), value: String(Number(rawByKey[k])) })),
  ];

  return (
    <div className="text-gray-700 space-y-3">
      {battingRows.length > 0 && (
        <div className="rounded bg-gray-50/90 border border-gray-200/80 overflow-hidden">
          <div className="px-2.5 py-1.5 border-b border-gray-200/80 bg-gray-100/80">
            <span className="text-xs font-semibold text-gray-700">Batting</span>
          </div>
          <div className="px-2.5 py-1.5">
            <div className="grid grid-cols-3 gap-x-6 gap-y-0">
              {battingRows.map(({ label, value }) => (
                <div key={label} className="flex justify-between gap-2 py-0.5">
                  <span className="text-gray-600 shrink-0">{label}</span>
                  <span className="tabular-nums font-medium text-gray-900 text-right min-w-[2.5rem]">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {hasPitchingData && pitchingRows.length > 0 && (
        <div className="rounded bg-gray-50/90 border border-gray-200/80 overflow-hidden">
          <div className="px-2.5 py-1.5 border-b border-gray-200/80 bg-gray-100/80">
            <span className="text-xs font-semibold text-gray-700">Pitching</span>
          </div>
          <div className="px-2.5 py-1.5">
            <div className="grid grid-cols-3 gap-x-6 gap-y-0">
              {pitchingRows.map(({ key, label, value }) => (
                <div key={key} className="flex justify-between gap-2 py-0.5">
                  <span className="text-gray-600 shrink-0">{label}</span>
                  <span className="tabular-nums font-medium text-gray-900 text-right min-w-[2.5rem]">{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export const GameDetailView: React.FC<GameDetailViewProps> = () => {
  const { gameId } = useParams<{ gameId: string }>();
  const navigate = useNavigate();
  const { currentTeam } = useTeamStore();
  
  const [game, setGame] = useState<Game | null>(null);
  const [loading, setLoading] = useState(true);
  const [lineup, setLineup] = useState<Array<{ order: number; player: Player | null }>>([]);
  const [playerStats, setPlayerStats] = useState<Map<string, StatWithId[]>>(new Map());
  const [calculatedStats, setCalculatedStats] = useState<Map<string, Record<string, number>>>(new Map());
  const [showAddStatsModal, setShowAddStatsModal] = useState(false);
  const [selectedPlayerCardId, setSelectedPlayerCardId] = useState<string | null>(null);
  const [playerCards, setPlayerCards] = useState<Map<string, string>>(new Map()); // player_id -> player_card_id
  const [allTeamPlayers, setAllTeamPlayers] = useState<Player[]>([]);
  const [editingPlayerId, setEditingPlayerId] = useState<string | null>(null);
  const [showSetLineupModal, setShowSetLineupModal] = useState(false);
  const [manualLineupDraft, setManualLineupDraft] = useState<(string | null)[]>(Array(9).fill(null));
  const [showImportCsvModal, setShowImportCsvModal] = useState(false);
  const [csvImporting, setCsvImporting] = useState(false);
  const [csvImportStatus, setCsvImportStatus] = useState<string | null>(null);
  const csvFileInputRef = useRef<HTMLInputElement>(null);
  const [expandedStatsPlayerIds, setExpandedStatsPlayerIds] = useState<Set<string>>(new Set());
  const [showReplaceStatsConfirm, setShowReplaceStatsConfirm] = useState(false);
  const pendingCsvFileRef = useRef<File | null>(null);

  useEffect(() => {
    if (gameId && currentTeam?.id) {
      loadGame();
      loadPlayerCards();
      loadAllTeamPlayers();
    }
  }, [gameId, currentTeam?.id]);

  useEffect(() => {
    if (game && currentTeam?.id && gameId) {
      loadLineup(game);
    }
  }, [game, currentTeam?.id, gameId]);

  useEffect(() => {
    if (gameId && currentTeam?.id) {
      loadGameStats();
    }
  }, [gameId, currentTeam?.id, lineup]);

  const loadGame = async () => {
    if (!gameId) return;
    
    setLoading(true);
    try {
      const gameData = await gameService.getGame(gameId);
      setGame(gameData);
    } catch (error) {
      console.error('Error loading game:', error);
      toast.error('Failed to load game');
    } finally {
      setLoading(false);
    }
  };

  const loadAllTeamPlayers = async () => {
    if (!currentTeam?.id) return;
    try {
      const { data, error } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', currentTeam.id)
        .order('last_name');
      if (!error) setAllTeamPlayers(data || []);
    } catch (e) {
      console.error('Error loading team players:', e);
    }
  };

  // Every game has its own lineup (game_lineup). Load from DB only. Show all players who were in the game (including currently "out"); "out" only affects new games.
  const loadLineup = useCallback(async (gameData: Game) => {
    if (!currentTeam?.id || !gameId) return;

    try {
      const { data: playersData, error: playersError } = await supabase
        .from('players')
        .select('*')
        .eq('team_id', currentTeam.id);

      if (playersError) {
        console.error('Error loading players:', playersError);
        return;
      }
      const playersList = playersData || [];

      const entries = await gameService.getGameLineup(gameData.id);
      const lineupEntries: Array<{ order: number; player: Player | null }> = entries.map((e) => {
        const player = playersList.find((p: Player) => p.id === e.player_id) || null;
        return { order: e.batting_order, player };
      });
      lineupEntries.sort((a, b) => a.order - b.order);
      setLineup(lineupEntries);
    } catch (error) {
      console.error('Error loading lineup:', error);
    }
  }, [currentTeam?.id, gameId]);

  const loadPlayerCards = async () => {
    if (!currentTeam?.id) return;

    try {
      const cards = await playerCardService.getPlayerCardsForTeam(currentTeam.id);
      const cardMap = new Map<string, string>();
      cards.forEach(card => {
        if (card.player?.id) {
          cardMap.set(card.player.id, card.id);
        }
      });
      setPlayerCards(cardMap);
    } catch (error) {
      console.error('Error loading player cards:', error);
    }
  };

  const loadGameStats = async () => {
    if (!gameId || !currentTeam?.id) return;

    try {
      // Load all stats for this game
      const { data: statsData, error } = await supabase
        .from('sport_stats')
        .select(`
          *,
          player_card:player_cards!inner(
            id,
            player:players(id, first_name, last_name, number)
          )
        `)
        .eq('team_id', currentTeam.id)
        .eq('game_id', gameId);

      if (error) {
        console.error('Error loading game stats:', error);
        return;
      }

      // Group stats by player (including stat IDs)
      const statsMap = new Map<string, StatWithId[]>();
      const calculatedMap = new Map<string, Record<string, number>>();

      (statsData || []).forEach((stat: any) => {
        const playerCardId = stat.player_card?.id;
        const playerId = stat.player_card?.player?.id;
        
        if (!playerCardId || !playerId) return;

        if (!statsMap.has(playerId)) {
          statsMap.set(playerId, []);
        }

        statsMap.get(playerId)!.push({
          id: stat.id,
          stat_key: stat.stat_key,
          stat_value: parseFloat(stat.stat_value.toString()),
        });
      });

      // Calculate stats for each player
      statsMap.forEach((stats, playerId) => {
        if (currentTeam?.sport) {
          // Convert to format expected by getCalculatedStats
          const statsForCalc = stats.map(s => ({ stat_key: s.stat_key, stat_value: s.stat_value }));
          calculatedMap.set(playerId, getCalculatedStats(currentTeam.sport, statsForCalc));
        }
      });

      setPlayerStats(statsMap);
      setCalculatedStats(calculatedMap);
    } catch (error) {
      console.error('Error loading game stats:', error);
    }
  };

  const handleStatsAdded = () => {
    loadGameStats();
    setShowAddStatsModal(false);
    setSelectedPlayerCardId(null);
    setEditingPlayerId(null);
  };

  const matchPlayer = (number: string, last: string, first: string): Player | null => {
    const numTrim = number.trim();
    const lastNorm = last.trim().toLowerCase();
    const firstNorm = first.trim().toLowerCase();
    const byNumber = numTrim !== '' && allTeamPlayers.find((p) => p.number != null && String(p.number).trim() === numTrim);
    if (byNumber) return byNumber;
    return allTeamPlayers.find(
      (p) =>
        (p.last_name ?? '').toLowerCase() === lastNorm &&
        (p.first_name ?? '').toLowerCase() === firstNorm
    ) ?? null;
  };

  const hasExistingGameStats = [...playerStats.values()].some((arr) => arr.length > 0);

  const runCsvImport = async (file: File) => {
    if (!gameId || !currentTeam?.id || !game) return;
    setCsvImporting(true);
    setCsvImportStatus('Reading file…');
    try {
      const text = await new Promise<string>((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () => resolve((reader.result as string) ?? '');
        reader.onerror = () => reject(reader.error);
        reader.readAsText(file);
      });
      setCsvImportStatus('Parsing CSV…');
      const parsed = parseGameChangerCsvToPlayerStats(text);
      if (parsed.length === 0) {
        toast.error('No player rows found in the CSV');
        setCsvImportStatus(null);
        setCsvImporting(false);
        return;
      }
      const noBattingButHasPitching =
        parsed.every((r) => r.batting.length === 0) && parsed.some((r) => r.pitching.length > 0);
      let imported = 0;
      const skipped: string[] = [];
      const date = game.date;
      const opponent = game.opponent_name ?? undefined;
      const season = game.season ?? undefined;
      for (const row of parsed) {
        const player = matchPlayer(row.number, row.last, row.first);
        if (!player) {
          skipped.push(`${row.first} ${row.last} (#${row.number})`);
          continue;
        }
        const playerCardId = playerCards.get(player.id);
        if (!playerCardId) {
          skipped.push(`${player.first_name} ${player.last_name} (no player card)`);
          continue;
        }
        setCsvImportStatus(`Importing ${player.first_name} ${player.last_name}…`);
        for (const { stat_key, value } of [...row.batting, ...row.pitching]) {
          await sportStatsService.addStat(playerCardId, currentTeam.id, stat_key, value, date, gameId, opponent, season);
        }
        imported += 1;
      }
      loadGameStats();
      setShowImportCsvModal(false);
      setShowReplaceStatsConfirm(false);
      setCsvImportStatus(null);
      setCsvImporting(false);
      if (imported === 0) {
        toast.error(
          'Could not import any data. The file format may be incorrect or no players matched. Check that the CSV has "Number", "Last", and "First" columns and that player names/numbers match your roster.'
        );
        return;
      }
      if (noBattingButHasPitching) {
        toast(
          'No batting data was found in this file; only pitching stats were imported. Check that the file has a "Batting" section in the header row.',
          { icon: '⚠️', duration: 6000 }
        );
      }
      if (skipped.length > 0) {
        toast.success(`Imported stats for ${imported} player(s). Skipped: ${skipped.slice(0, 3).join(', ')}${skipped.length > 3 ? ` and ${skipped.length - 3} more` : ''}`);
      } else {
        toast.success(`Imported stats for ${imported} player(s).`);
      }
    } catch (err: any) {
      console.error('CSV import error:', err);
      toast.error(err?.message ?? 'Failed to import CSV');
      setCsvImportStatus(null);
      setCsvImporting(false);
    }
  };

  const handleCsvFileSelected = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !gameId || !currentTeam?.id || !game) return;

    if (hasExistingGameStats) {
      pendingCsvFileRef.current = file;
      setShowReplaceStatsConfirm(true);
      return;
    }

    await runCsvImport(file);
  };

  const handleConfirmReplaceCsvImport = async () => {
    const file = pendingCsvFileRef.current;
    if (!file || !gameId || !currentTeam?.id || !game) {
      setShowReplaceStatsConfirm(false);
      pendingCsvFileRef.current = null;
      return;
    }
    setCsvImporting(true);
    setCsvImportStatus('Clearing existing stats…');
    try {
      await sportStatsService.deleteStatsByGameId(gameId);
      pendingCsvFileRef.current = null;
      await runCsvImport(file);
    } catch (err: any) {
      console.error('Replace/import error:', err);
      toast.error(err?.message ?? 'Failed to replace stats');
      setCsvImportStatus(null);
      setCsvImporting(false);
      setShowReplaceStatsConfirm(false);
      pendingCsvFileRef.current = null;
    }
  };

  const handleCancelReplaceCsvImport = () => {
    setShowReplaceStatsConfirm(false);
    pendingCsvFileRef.current = null;
  };

  const openSetLineupModal = () => {
    const maxOrder = lineup.length ? Math.max(...lineup.map((e) => e.order), 9) : 9;
    const draft: (string | null)[] = Array(maxOrder).fill(null);
    lineup.forEach(({ order, player }) => {
      const idx = order - 1;
      if (idx >= 0 && player) draft[idx] = player.id;
    });
    setManualLineupDraft(draft);
    setShowSetLineupModal(true);
  };

  const fillDraftFromDepthChart = useCallback(async () => {
    if (!currentTeam?.id) return;
    try {
      const { data: depthChartData, error } = await supabase
        .from('depth_chart')
        .select('*')
        .eq('team_id', currentTeam.id)
        .eq('unit', 'Lineup')
        .eq('depth', 1)
        .order('position', { ascending: true });

      if (error) {
        console.error('Error loading depth chart:', error);
        toast.error('Could not load depth chart');
        return;
      }

      const draft: (string | null)[] = Array(Math.max(9, (depthChartData || []).length)).fill(null);
      (depthChartData || []).forEach((entry: DepthChartEntry) => {
        const idx = (parseInt(entry.position, 10) || 1) - 1;
        if (idx >= 0) draft[idx] = entry.player_id;
      });
      setManualLineupDraft(draft);
      toast.success('Filled from Depth Chart. Edit if needed, then Save.');
    } catch (e) {
      toast.error('Could not load depth chart');
    }
  }, [currentTeam?.id]);

  const saveManualLineup = async () => {
    if (!gameId || !game) return;
    const entries: GameLineupEntry[] = manualLineupDraft
      .map((playerId, i) => (playerId ? { batting_order: i + 1, player_id: playerId } : null))
      .filter((e): e is GameLineupEntry => e != null);
    try {
      await gameService.setGameLineup(gameId, entries);
      if (game.lineup_source !== 'manual') {
        await gameService.updateGame(gameId, { lineup_source: 'manual' });
        setGame((g) => (g ? { ...g, lineup_source: 'manual' } : null));
      }
      toast.success('Lineup saved');
      setShowSetLineupModal(false);
      loadLineup({ ...game, lineup_source: 'manual' });
    } catch (e) {
      toast.error('Failed to save lineup');
    }
  };

  const playersWithCards = allTeamPlayers.filter((p) => playerCards.has(p.id));
  const lineupIsEmpty = lineup.length === 0;
  const availablePlayersForLineup = useMemo(
    () => allTeamPlayers.filter((p) => p.injury_status !== INJURY_STATUS.OUT),
    [allTeamPlayers]
  );

  // Game details: all batting order entries (not just 9) for display
  const gameDetailsLineupEntries = useMemo(
    () =>
      lineup
        .filter((e): e is { order: number; player: Player } => e.player != null)
        .sort((a, b) => a.order - b.order),
    [lineup]
  );

  const toggleStatsExpanded = useCallback((playerId: string) => {
    setExpandedStatsPlayerIds((prev) => {
      const next = new Set(prev);
      if (next.has(playerId)) next.delete(playerId);
      else next.add(playerId);
      return next;
    });
  }, []);

  // Record-stats list order: game lineup first (batting order), then other players (main roster before subs, then by number)
  const recordStatsOrderedPlayers = (() => {
    const lineupIds = new Set(lineup.map(({ player }) => player?.id).filter(Boolean) as string[]);
    const inLineupOrder = lineup
      .map(({ order, player }) => (player && playerCards.has(player.id) ? { order, player } : null))
      .filter((x): x is { order: number; player: Player } => x != null)
      .sort((a, b) => a.order - b.order)
      .map(({ player }) => player);
    const notInLineup = playersWithCards
      .filter((p) => !lineupIds.has(p.id))
      .sort((a, b) => {
        // Main roster (non-subs) before subs, then by jersey number
        const aSub = (a as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
        const bSub = (b as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
        if (aSub !== bSub) return aSub - bSub;
        return (a.number ?? 999) - (b.number ?? 999);
      });
    return [...inLineupOrder, ...notInLineup];
  })();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary"></div>
      </div>
    );
  }

  if (!game) {
    return (
      <div className="text-center py-12">
        <p className="text-gray-600">Game not found</p>
        <button
          onClick={() => navigate('/games')}
          className="mt-4 btn-primary"
        >
          Back to Games
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate('/games')}
          className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold text-gray-900">
            {game.opponent_name}
          </h1>
          <div className="flex items-center gap-4 mt-2 text-sm text-gray-600">
            <span>{new Date(game.date).toLocaleDateString('en-US', {
              weekday: 'long',
              year: 'numeric',
              month: 'long',
              day: 'numeric',
            })}</span>
            {game.game_time && (
              <span>{new Date(`2000-01-01T${game.game_time}`).toLocaleTimeString('en-US', {
                hour: 'numeric',
                minute: '2-digit',
              })}</span>
            )}
            {game.location && (
              <span>{game.location}</span>
            )}
            <span className="px-2 py-1 bg-gray-100 rounded text-xs">
              {game.home_away === 'home' ? 'Home' : 'Away'}
            </span>
          </div>
        </div>
      </div>

      {/* Lineup and Stats */}
      <div className="bg-white rounded-lg shadow p-6">
        <h2 className="text-lg font-semibold text-gray-900 mb-4">Lineup & Statistics</h2>
        <div className="flex flex-wrap items-center gap-2 mb-4">
          {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (
            <button
              type="button"
              onClick={() => setShowImportCsvModal(true)}
              className="btn-secondary flex items-center gap-1.5 text-sm"
            >
              <Upload size={16} />
              Import CSV
            </button>
          )}
          {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (
            <button
              type="button"
              onClick={openSetLineupModal}
              className="btn-secondary flex items-center gap-1.5 text-sm"
            >
              <ListOrdered size={16} />
              {lineup.length > 0 ? 'Edit lineup' : 'Set lineup'}
            </button>
          )}
        </div>

        {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (
          <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-lg text-sm text-blue-800">
            <p className="font-medium">Upload your GameChanger data here.</p>
            <p className="mt-1 text-blue-700">Use the <strong>Import CSV</strong> button to upload a GameChanger stats export. Batting and pitching data that we support will be imported for this game. Note: fielding and some other advanced stats are currently not supported.</p>
          </div>
        )}

        {lineupIsEmpty && (currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && (
          <div className="mb-4 p-4 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
            <p className="font-medium">No lineup set for this game.</p>
            <p className="mt-1">Click <strong>Set lineup</strong> to choose your batting order. You can copy from your Depth Chart in the modal.</p>
          </div>
        )}

        {(currentTeam?.sport === 'Baseball' || currentTeam?.sport === 'Softball') && availablePlayersForLineup.length > 0 ? (
          <>
            {gameDetailsLineupEntries.length > 0 && (
            <div className="space-y-1">
              {gameDetailsLineupEntries.map(({ order, player }) => {
                  const isExpanded = expandedStatsPlayerIds.has(player.id);
                  const cardId = playerCards.get(player.id);
                  return (
                    <div key={player.id} className="rounded border border-gray-200 bg-white overflow-hidden">
                      <div className="flex items-center gap-2 p-2 w-full text-left">
                        <span className="flex-shrink-0 w-6 text-sm font-semibold text-gray-500 tabular-nums">{order}.</span>
                        <div className="flex items-center gap-2 flex-1 min-w-0">
                          {player.number != null ? (
                            <span className="font-bold text-primary text-sm min-w-[2rem]">#{player.number}</span>
                          ) : (
                            <User size={16} className="text-gray-400 flex-shrink-0" />
                          )}
                          <span className="font-medium text-sm text-gray-900">
                            {player.first_name ?? ''} {player.last_name ?? ''}
                          </span>
                          {player.injury_status && player.injury_status !== 'H' && (
                            <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="xs" showLabel={false} />
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={(e) => {
                            e.stopPropagation();
                            toggleStatsExpanded(player.id);
                          }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                          aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
                        >
                          {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                        </button>
                        {cardId && (
                          <button
                            type="button"
                            onClick={() => {
                              setSelectedPlayerCardId(cardId);
                              setEditingPlayerId(player.id);
                              setShowAddStatsModal(true);
                            }}
                            className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                            aria-label="Edit stats"
                          >
                            <Edit2 size={16} />
                          </button>
                        )}
                      </div>
                      {isExpanded && (
                        <div className="px-2 pb-2 pt-0 border-t border-gray-100 bg-gray-50/80">
                          <GameDetailsPlayerStatsSummary
                            playerId={player.id}
                            rawStats={playerStats.get(player.id) || []}
                            calculated={calculatedStats.get(player.id) || {}}
                            sport={currentTeam?.sport}
                          />
                        </div>
                      )}
                    </div>
                  );
                })}
            </div>
            )}
          </>
        ) : recordStatsOrderedPlayers.length > 0 ? (
          <>
            <p className="text-xs text-gray-500 mb-3">Click a player to add or edit statistics. Use the chevron to expand and view stats.</p>
            <div className="space-y-1">
              {recordStatsOrderedPlayers.map((player) => {
                const lineupSpot = lineup.find((e) => e.player?.id === player.id);
                const isExpanded = expandedStatsPlayerIds.has(player.id);
                const cardId = playerCards.get(player.id);
                return (
                  <div key={player.id} className="rounded border border-gray-200 bg-white overflow-hidden">
                    <div className="flex items-center gap-2 p-2 w-full text-left">
                      <span className="flex-shrink-0 w-6 text-sm font-semibold text-gray-500 tabular-nums">
                        {lineupSpot ? `${lineupSpot.order}.` : '—'}
                      </span>
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        {player.number != null ? (
                          <span className="font-bold text-primary text-sm min-w-[2rem]">#{player.number}</span>
                        ) : (
                          <User size={16} className="text-gray-400 flex-shrink-0" />
                        )}
                        <span className="font-medium text-sm text-gray-900">
                          {player.first_name ?? ''} {player.last_name ?? ''}
                        </span>
                        {player.injury_status && player.injury_status !== 'H' && (
                          <InjuryBadge injuryStatus={player.injury_status as InjuryStatus} size="xs" showLabel={false} />
                        )}
                      </div>
                      <button
                        type="button"
                        onClick={(e) => {
                          e.stopPropagation();
                          toggleStatsExpanded(player.id);
                        }}
                        className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                        aria-label={isExpanded ? 'Collapse stats' : 'Expand stats'}
                      >
                        {isExpanded ? <ChevronDown size={18} /> : <ChevronRight size={18} />}
                      </button>
                      {cardId && (
                        <button
                          type="button"
                          onClick={() => {
                            setSelectedPlayerCardId(cardId);
                            setEditingPlayerId(player.id);
                            setShowAddStatsModal(true);
                          }}
                          className="p-1.5 rounded hover:bg-gray-100 text-gray-500 hover:text-primary transition-colors flex-shrink-0"
                          aria-label="Edit stats"
                        >
                          <Edit2 size={16} />
                        </button>
                      )}
                    </div>
                    {isExpanded && (
                      <div className="px-2 pb-2 pt-0 border-t border-gray-100 bg-gray-50/80">
                        <GameDetailsPlayerStatsSummary
                          playerId={player.id}
                          rawStats={playerStats.get(player.id) || []}
                          calculated={calculatedStats.get(player.id) || {}}
                          sport={currentTeam?.sport}
                        />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <p className="text-sm text-gray-500">No players with player cards yet. Add player cards from Roster to record stats for this game.</p>
        )}
      </div>

      {/* Set manual lineup modal */}
      {showSetLineupModal && game && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[85vh] flex flex-col">
            <div className="flex-shrink-0 border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">Set batting order</h3>
              <button type="button" onClick={() => setShowSetLineupModal(false)} className="p-1 hover:bg-gray-100 rounded">
                <X size={20} />
              </button>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto p-4 space-y-3">
              <button
                type="button"
                onClick={fillDraftFromDepthChart}
                className="w-full text-sm text-primary border border-primary/50 rounded-lg py-2 hover:bg-primary/5 transition-colors"
              >
                Fill from Depth Chart
              </button>
              {manualLineupDraft.map((_, idx) => {
                const order = idx + 1;
                return (
                  <div key={order} className="flex items-center gap-3">
                    <span className="text-sm font-medium text-gray-500 w-6">{order}.</span>
                    <select
                      value={manualLineupDraft[order - 1] ?? ''}
                      onChange={(e) => {
                        const val = e.target.value || null;
                        setManualLineupDraft((prev) => {
                          const next = [...prev];
                          next[order - 1] = val;
                          return next;
                        });
                      }}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm"
                    >
                      <option value="">— Select player —</option>
                      {[...allTeamPlayers]
                        .filter((p) => p.injury_status !== INJURY_STATUS.OUT)
                        .sort((a, b) => {
                          const aSub = (a as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
                          const bSub = (b as Player & { is_sub?: boolean }).is_sub ? 1 : 0;
                          if (aSub !== bSub) return aSub - bSub;
                          return (a.number ?? 999) - (b.number ?? 999);
                        })
                        .map((p) => {
                          const injuryLabel = p.injury_status && p.injury_status !== 'H' ? ` (${p.injury_status})` : '';
                          return (
                            <option key={p.id} value={p.id} disabled={manualLineupDraft.includes(p.id) && manualLineupDraft[order - 1] !== p.id}>
                              {p.first_name} {p.last_name} {p.number != null ? `#${p.number}` : ''}{injuryLabel}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                );
              })}
              <button
                type="button"
                onClick={() => setManualLineupDraft((prev) => [...prev, null])}
                className="text-sm text-primary hover:underline"
              >
                + Add batting spot
              </button>
            </div>
            <div className="flex-shrink-0 p-4 border-t border-gray-200 flex gap-2 bg-white">
              <button type="button" onClick={() => setShowSetLineupModal(false)} className="flex-1 btn-secondary py-2">
                Cancel
              </button>
              <button type="button" onClick={saveManualLineup} className="flex-1 btn-primary py-2">
                Save lineup
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Import CSV Modal */}
      {showImportCsvModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
            <div className="sticky top-0 bg-white border-b border-gray-200 px-4 py-3 flex items-center justify-between">
              <h3 className="text-lg font-semibold">
                {showReplaceStatsConfirm ? 'Replace existing stats?' : 'Import GameChanger CSV'}
              </h3>
              <button
                type="button"
                onClick={() => {
                  if (!csvImporting) {
                    setShowImportCsvModal(false);
                    setShowReplaceStatsConfirm(false);
                    pendingCsvFileRef.current = null;
                  }
                }}
                className="p-1 hover:bg-gray-100 rounded disabled:opacity-50"
                disabled={csvImporting}
              >
                <X size={20} />
              </button>
            </div>
            <div className="p-4 space-y-4">
              {showReplaceStatsConfirm ? (
                <>
                  <p className="text-sm text-gray-700">
                    This game already has stats recorded. Are you sure you want to replace the existing stats with the GameChanger stats?
                  </p>
                  <div className="rounded-lg border-2 border-red-200 bg-red-50 p-3">
                    <p className="text-sm font-bold text-red-800">This cannot be undone.</p>
                  </div>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      onClick={handleCancelReplaceCsvImport}
                      disabled={csvImporting}
                      className="flex-1 btn-secondary py-2"
                    >
                      Cancel
                    </button>
                    <button
                      type="button"
                      onClick={handleConfirmReplaceCsvImport}
                      disabled={csvImporting}
                      className="flex-1 btn-primary py-2"
                    >
                      Replace and import
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <p className="text-sm text-gray-700">
                    Upload your GameChanger data here. Use a stats export from GameChanger (CSV). Batting and pitching stats that match our app will be imported for this game; fielding and other columns are not imported yet.
                  </p>
                  <input
                    ref={csvFileInputRef}
                    type="file"
                    accept=".csv"
                    className="hidden"
                    onChange={handleCsvFileSelected}
                  />
                  <button
                    type="button"
                    onClick={() => csvFileInputRef.current?.click()}
                    disabled={csvImporting}
                    className="btn-primary w-full flex items-center justify-center gap-2 py-2"
                  >
                    <Upload size={18} />
                    {csvImporting ? 'Importing…' : 'Choose CSV file'}
                  </button>
                </>
              )}
              {csvImportStatus && (
                <p className="text-sm text-gray-500">{csvImportStatus}</p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Add/Edit Stats Modal */}
      {currentTeam && showAddStatsModal && selectedPlayerCardId && (() => {
        const editingPlayer = recordStatsOrderedPlayers.find((p) => playerCards.get(p.id) === selectedPlayerCardId);
        const playerName = editingPlayer ? `${editingPlayer.first_name ?? ''} ${editingPlayer.last_name ?? ''}`.trim() || undefined : undefined;
        return (
          <AddGameStatsModal
            isOpen={showAddStatsModal}
            onClose={() => {
              setShowAddStatsModal(false);
              setSelectedPlayerCardId(null);
              setEditingPlayerId(null);
            }}
            playerCardId={selectedPlayerCardId}
            teamId={currentTeam.id}
            sport={currentTeam.sport}
            onStatsAdded={handleStatsAdded}
            gameId={gameId}
            existingStats={editingPlayerId ? playerStats.get(editingPlayerId) || [] : undefined}
            playerName={playerName}
          />
        );
      })()}
    </div>
  );
};

